// src/components/QposSocket.tsx

import React, {
    useEffect,
    useContext,
    useRef,
} from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import useSecureStorage from '../hooks/useSecureStorage';
import { useDispatch } from 'react-redux';
import {
    setWalletAddress,
    setConnected,
    setQpinBuyPending,
    setQpinSell,
    setQpinBuyConfirm,
} from '../store'; // 스토어 export에 맞춰 경로 유지

const QposSocket: React.FC = () => {
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const location = useLocation();

    const { getWalletAddress } = useSecureStorage();
    const dispatch = useDispatch();

    // 소켓 인스턴스를 재렌더링 사이에 유지
    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        let isMounted = true;
        let pingInterval: ReturnType<typeof setInterval> | null = null;

        const initSocket = async () => {
            const address: string | null = await getWalletAddress();

            if (!isMounted) return;
            if (!address) {
                console.log('지갑 주소 없음, 소켓 구독 건너뜀');
                return;
            }

            console.log('지갑주소', address);
            dispatch(setWalletAddress(address));

            // 이미 열려 있다면 다시 열지 않음
            if (socketRef.current) {
                return;
            }

            const socket = io('wss://dev.qpos-walletserver.r4r4.kr/qpin', {
                transports: ['websocket', 'polling'],
                reconnection: true,
            });

            socketRef.current = socket;

            // 연결
            socket.on('connect', () => {
                console.log('qpin 소켓 연결됨', socket.id);
                dispatch(setConnected(true));

                // 기존 interval 있으면 정리
                if (pingInterval) {
                    clearInterval(pingInterval);
                }

                // 1분마다 ping 전송
                pingInterval = setInterval(() => {
                    if (socket.connected) {
                        socket.emit('ping');
                        console.log('📤 ping 전송');
                    } else {
                        if (pingInterval) {
                            clearInterval(pingInterval);
                            pingInterval = null;
                        }
                    }
                }, 60000); // 1분 = 60000ms

                socket.emit('subscribe:wallet', {
                    wallet_address: address,
                });
            });

            socket.on('disconnect', () => {
                console.log('qpin 소켓 끊김');
                dispatch(setConnected(false));
                if (pingInterval) {
                    clearInterval(pingInterval);
                    pingInterval = null;
                }
            });

            // Qpin 생성 이벤트
            socket.on('qpin:generated', (data: any) => {
                console.log('Qpin 생성:', data);

                // 서버에서 넘어오는 필드명이 다르면 data.type 부분만 맞게 바꾸면 됨
                const type = data?.type;

                if (type === 'buy-pending') {
                    dispatch(setQpinBuyPending(data));
                } else if (type === 'sell') {
                    dispatch(setQpinSell(data));
                }
            });

            // Qpin 확인 완료 이벤트
            socket.on('qpin:confirmed', (data: any) => {
                console.log('Qpin 확인 완료:', data);

                const type = data?.type;

                if (type === 'buy-confirm') {
                    dispatch(setQpinBuyConfirm(data));
                }
            });
        };

        initSocket();

        return () => {
            isMounted = false;
            if (pingInterval) {
                clearInterval(pingInterval);
            }
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [getWalletAddress, dispatch]);

    return null; // 화면에 아무것도 안 그려도 됨 (전역 소켓 전용 컴포넌트)
};

export default QposSocket;
